import { useState, useMemo, useEffect } from 'react'
import { ELEMENTS, RARITY_CONFIG } from '../types'
import type { Creature, Element, CreatureRarity, ZoneId, EvolutionStage } from '../types'
import CreatureDetail from '../components/ui/CreatureDetail'
import { CreatureAPI } from '../api/client'

const P = (n: string) => `/assets/kenney/creatures/portraits/${n}.png`
const CREATURE_PORTRAITS: Record<string, string> = {
  'rell': P('rell'),           'crag': P('crag'),
  'flick': P('flick'),         'warden': P('warden'),
  'orrin': P('orrin'),         'null': P('null'),
  'mist': P('mist'),           'scorch': P('scorch'),
  'voss-manta': P('voss-manta'), 'origin-founding': P('origin-founding'),
  'snip': P('snip'),
  // name-based lookups (creature.name.toLowerCase())
  'origin': P('origin-founding'),
}

const ELEMENT_ICONS: Record<string, string> = {
  fire: '/assets/icons/element-fire.png',
  water: '/assets/icons/element-water.png',
  earth: '/assets/icons/element-earth.png',
  air: '/assets/icons/element-air.png',
  void: '/assets/icons/element-void.png',
  aether: '/assets/icons/element-aether.png',
}

const RARITY_ORDER: Record<CreatureRarity, number> = {
  mythic: 5, apex: 4, rift: 3, surge: 2, drift: 1,
}

type SortKey = 'rarity' | 'condition' | 'bond' | 'default'

const ELEMENT_OPTS: { label: string; value: Element | 'all' }[] = [
  { label: 'All',                                        value: 'all' },
  ...(['fire','water','earth','air','void','aether'] as Element[]).map(e => ({
    label: `${ELEMENTS[e].emoji} ${ELEMENTS[e].label}`,
    value: e,
  })),
]

const RARITY_OPTS: { label: string; value: CreatureRarity | 'all' }[] = [
  { label: 'All',    value: 'all' },
  { label: 'Drift',  value: 'drift' },
  { label: 'Surge',  value: 'surge' },
  { label: 'Rift',   value: 'rift' },
  { label: 'Apex',   value: 'apex' },
  { label: 'Mythic', value: 'mythic' },
]

export default function CreatureVault() {
  const [selected, setSelected] = useState<Creature | null>(null)
  const [elementFilter, setElementFilter] = useState<Element | 'all'>('all')
  const [rarityFilter, setRarityFilter] = useState<CreatureRarity | 'all'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('rarity')
  const [vault, setVault] = useState<Creature[]>([])
  const [loading, setLoading] = useState(true)
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    CreatureAPI.getMyCreatures().then(res => {
      if (res.success && res.data) {
        const creatures = (res.data as unknown as Array<{
          id: string; templateId: string; name: string; species: string
          element: string; zone: string; rarity: string; evolutionStage: number
          stats: Creature['stats']; moves: Creature['moves']
          bondedCharacterId: string | null; bondLevel: number
          condition: number; isTrading: boolean; isSoulbound: boolean
          description: string; isFoundingExclusive?: boolean
        }>).map(r => ({
          id: r.id,
          name: r.name,
          species: r.species,
          element: r.element as Element,
          zone: r.zone as ZoneId,
          rarity: r.rarity as CreatureRarity,
          evolutionStage: r.evolutionStage as EvolutionStage,
          stats: r.stats,
          moves: r.moves,
          bondedCharacterId: r.bondedCharacterId,
          bondLevel: r.bondLevel,
          condition: r.condition,
          isTrading: r.isTrading,
          isSoulbound: r.isSoulbound,
          description: r.description,
          isFoundingExclusive: r.isFoundingExclusive ?? false,
        } satisfies Creature))
        setVault(creatures)
      } else if (!res.success) {
        setApiError(res.error ?? 'Could not load vault')
      }
    }).finally(() => setLoading(false))
  }, [])

  const founding = vault.filter(c => c.isFoundingExclusive)
  const regular  = vault.filter(c => !c.isFoundingExclusive)

  const filtered = useMemo(() => {
    let list = [...regular]
    if (elementFilter !== 'all') list = list.filter(c => c.element === elementFilter)
    if (rarityFilter !== 'all') list = list.filter(c => c.rarity === rarityFilter)
    if (sortKey === 'rarity')    list.sort((a, b) => RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity])
    else if (sortKey === 'condition') list.sort((a, b) => b.condition - a.condition)
    else if (sortKey === 'bond')      list.sort((a, b) => b.bondLevel - a.bondLevel)
    return list
  }, [elementFilter, rarityFilter, sortKey, regular])

  const hasFilters = elementFilter !== 'all' || rarityFilter !== 'all'

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Beast Vault</h1>
          {loading && <span className="text-[10px] text-[#8A8AA5] animate-pulse" style={{ fontFamily: 'Space Mono, monospace' }}>Syncing…</span>}
          {apiError && <span className="text-[10px] text-red-400" style={{ fontFamily: 'Space Mono, monospace' }}>⚠ offline</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {filtered.length + founding.length} / {vault.length}
          </span>
        </div>
      </div>

      {/* Element Filter - Horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {ELEMENT_OPTS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setElementFilter(opt.value)}
            className={`font-bold text-xs px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              elementFilter === opt.value
                ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Rarity Filter - Horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 py-2">
        {RARITY_OPTS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setRarityFilter(opt.value)}
            className={`font-bold text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              rarityFilter === opt.value ? '' : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20'
            }`}
            style={rarityFilter === opt.value ? {
              borderColor: opt.value === 'all' ? '#00F0FF' : (RARITY_CONFIG[opt.value as CreatureRarity]?.color ?? '#00F0FF'),
              color: opt.value === 'all' ? '#00F0FF' : (RARITY_CONFIG[opt.value as CreatureRarity]?.color ?? '#00F0FF'),
              background: opt.value === 'all' ? 'rgba(0,240,255,0.05)' : `${RARITY_CONFIG[opt.value as CreatureRarity]?.color ?? '#00F0FF'}10`,
            } : undefined}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Sort - Horizontal scroll */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {[
          { key: 'rarity', label: 'Rarity' },
          { key: 'condition', label: 'Condition' },
          { key: 'bond', label: 'Bond' },
          { key: 'default', label: 'Default' },
        ].map(opt => (
          <button
            key={opt.key}
            onClick={() => setSortKey(opt.key as SortKey)}
            className={`font-bold text-[10px] px-3 py-1 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              sortKey === opt.key
                ? 'border-[#00F0FF] border-opacity-60 text-[#00F0FF] bg-[#00F0FF] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* ORIGIN founding creature */}
        {founding.length > 0 && !hasFilters && (
          <div className="mb-4">
            <p className="text-[10px] text-[#FFE800] uppercase tracking-wider mb-2" style={{ fontFamily: 'Space Mono, monospace' }}>✨ Founding Exclusive</p>
            {founding.map(c => (
              <CreatureCard key={c.id} creature={c} featured onSelect={setSelected} />
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">🐾</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No creatures match</p>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Try different filters.</p>
            <button
              onClick={() => { setElementFilter('all'); setRarityFilter('all') }}
              className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ background: 'rgba(58,58,90,0.3)', color: '#8A8AA5', fontFamily: 'Space Mono, monospace' }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(c => (
              <CreatureCard key={c.id} creature={c} onSelect={setSelected} />
            ))}
          </div>
        )}
      </div>

      <CreatureDetail creature={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

function CreatureCard({
  creature, featured = false, onSelect,
}: { creature: Creature; featured?: boolean; onSelect: (c: Creature) => void }) {
  const el = ELEMENTS[creature.element]
  const rarity = RARITY_CONFIG[creature.rarity]
  const conditionColor = creature.condition >= 80 ? '#39FF14' : creature.condition >= 50 ? '#00F0FF' : '#FF4800'
  const isOrigin = creature.isFoundingExclusive

  return (
    <div
      onClick={() => onSelect(creature)}
      className={`group relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-240 ${
        featured ? 'ring-1' : ''
      }`}
      style={{
        background: isOrigin
          ? `linear-gradient(160deg, rgba(255,232,0,0.08), rgba(13,13,21,0.95) 60%)`
          : `linear-gradient(160deg, rgba(13,13,21,0.95), rgba(8,8,14,0.98))`,
        borderColor: isOrigin ? '#FFE80060' : 'rgba(58,58,90,0.5)',
        boxShadow: isOrigin 
          ? '0 0 24px rgba(255,232,0,0.15), 0 0 0 1px rgba(0,0,0,0.5)' 
          : '0 0 0 1px rgba(0,0,0,0.5)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = isOrigin
          ? `0 0 32px ${el.color}50, 0 0 0 1px ${el.color}40`
          : `0 0 16px ${el.color}40, 0 0 0 1px ${el.color}30`;
        e.currentTarget.style.borderColor = isOrigin ? '#FFE80080' : `${el.color}50`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = isOrigin 
          ? '0 0 24px rgba(255,232,0,0.15), 0 0 0 1px rgba(0,0,0,0.5)' 
          : '0 0 0 1px rgba(0,0,0,0.5)';
        e.currentTarget.style.borderColor = isOrigin ? '#FFE80060' : 'rgba(58,58,90,0.5)';
      }}
    >
      {/* Glow layer */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-240 pointer-events-none"
        style={{ 
          background: isOrigin
            ? `radial-gradient(circle at 50% 0%, #FFE80012, transparent 70%)`
            : `radial-gradient(circle at 50% 0%, ${el.color}08, transparent 70%)` 
        }}
      />

      {/* ORIGIN shimmer effect */}
      {isOrigin && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5 pointer-events-none"
          style={{ 
            background: `linear-gradient(90deg, transparent, #FFE800, #00F0FF, #BF00FF, transparent)`,
            boxShadow: '0 0 8px #FFE800',
          }}
        />
      )}
      
      <div className="relative z-10 p-5">
        {/* Header: sprite + name */}
        <div className="flex items-start gap-4 mb-3">
          <div className="flex-shrink-0">
            {(() => {
              const src = CREATURE_PORTRAITS[creature.name.toLowerCase().replace(/\s+/g, '-')]
                ?? CREATURE_PORTRAITS[creature.name.toLowerCase()]
              const sz = featured ? 96 : 80
              return src ? (
                <img src={src} alt={creature.name} width={sz} height={sz}
                  className="object-contain character-float"
                  style={{ 
                    width: sz, height: sz, 
                    filter: isOrigin 
                      ? `drop-shadow(0 0 16px #FFE80060) drop-shadow(0 0 8px ${el.color}40)`
                      : `drop-shadow(0 0 12px ${el.color}50)`,
                    imageRendering: 'crisp-edges',
                  }}
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div style={{ width: sz, height: sz, borderRadius: '50%',
                  background: `radial-gradient(circle, ${el.color}40 0%, ${el.color}10 60%, transparent 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: sz * 0.45 }}>{el.emoji}</div>
              )
            })()}
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <h3 className="font-bold text-lg text-[#EAF6FF] uppercase tracking-tight" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {creature.name}
              </h3>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider"
                  style={{ 
                    borderColor: `${el.color}40`,
                    background: `${el.color}10`,
                    color: el.color,
                    fontFamily: 'Space Mono, monospace',
                  }}
                >
                  <img src={ELEMENT_ICONS[creature.element]} alt={el.label} className="w-3.5 h-3.5" />
                  {el.label}
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" 
                  style={{ 
                    color: rarity.color, 
                    background: `${rarity.color}15`,
                    fontFamily: 'Space Mono, monospace',
                  }}
                >
                  {rarity.label}
                </span>
              </div>
            </div>
            <p className="text-xs text-[#8A8AA5] mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{creature.species}</p>
            {/* Badges */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {creature.isFoundingExclusive && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border"
                  style={{ 
                    borderColor: '#FFE80060',
                    background: '#FFE80015',
                    color: '#FFE800',
                    fontFamily: 'Space Mono, monospace',
                  }}
                >
                  ✨ ORIGIN
                </span>
              )}
              {creature.isSoulbound && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border text-[#5A5A7A]"
                  style={{ borderColor: 'rgba(90,90,122,0.5)', fontFamily: 'Space Mono, monospace' }}>
                  🔒 Soulbound
                </span>
              )}
              {creature.isTrading && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border"
                      style={{ borderColor: '#39FF1460', color: '#39FF14', fontFamily: 'Space Mono, monospace' }}>
                  ⚖️ Listed
                </span>
              )}
            </div>
          </div>
        </div>

      {/* Description */}
      <p className="text-body text-xs italic mb-4 line-clamp-2 opacity-60 leading-relaxed">
        "{creature.description}"
      </p>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'ATK', value: creature.stats.attack },
          { label: 'DEF', value: creature.stats.defense },
          { label: 'SPD', value: creature.stats.speed },
          { label: 'BOND', value: creature.stats.bond },
        ].map(stat => (
          <div key={stat.label} className="text-center">
            <p className="text-label text-[10px] mb-0.5">{stat.label}</p>
            <p className="font-display font-black text-xs" style={{ color: el.color }}>{stat.value}</p>
            <div className="stat-bar mt-1">
              <div className="stat-bar-fill" style={{ width: `${stat.value}%`, background: `linear-gradient(90deg, ${el.color}60, ${el.color})` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Evolution track */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-label text-xs">Evolution Stage {creature.evolutionStage}</p>
        <div className="flex gap-1.5">
          {[1, 2, 3].map(stage => (
            <div
              key={stage}
              className={stage <= creature.evolutionStage ? 'awakening-node-active' : 'awakening-node'}
              style={stage <= creature.evolutionStage ? {
                background: `linear-gradient(90deg, ${el.color}, ${el.color}80)`,
                boxShadow: `0 0 6px ${el.color}50`,
              } : undefined}
            />
          ))}
        </div>
      </div>

      {/* Bond level meter */}
      <div className="mb-3">
        <div className="flex justify-between mb-1">
          <p className="text-label text-xs">
            Bond{creature.bondedCharacterId ? ` — ${creature.bondedCharacterId.replace(/-/g, ' ')}` : ''}
          </p>
          <p className="font-display font-bold text-xs" style={{ color: el.color }}>{creature.bondLevel}/100</p>
        </div>
        <div className="stat-bar">
          <div
            className="stat-bar-fill"
            style={{ width: `${creature.bondLevel}%`, background: `linear-gradient(90deg, ${el.color}70, ${el.color})` }}
          />
        </div>
      </div>

      {/* Footer: condition + trade button */}
      <div className="flex items-center justify-between pt-3 border-t border-white border-opacity-5">
        <div className="flex items-center gap-2">
          <p className="text-label text-xs">Condition</p>
          <p className="font-display font-bold text-xs" style={{ color: conditionColor }}>
            {creature.condition}/100
          </p>
        </div>
        <button
          disabled={creature.isSoulbound}
          onClick={e => { e.stopPropagation() }}
          title={creature.isSoulbound ? 'Soulbound — cannot be traded' : 'List for trade'}
          className={`font-display font-bold text-[10px] px-3 py-1.5 rounded-ab border transition-all ${
            creature.isSoulbound
              ? 'border-white border-opacity-5 text-ab-text-dim opacity-40 cursor-not-allowed'
              : creature.isTrading
                ? 'border-opacity-60 cursor-pointer'
                : 'border-white border-opacity-15 text-ab-text-dim hover:text-ab-text hover:border-opacity-30 cursor-pointer'
          }`}
          style={creature.isTrading ? { borderColor: '#39FF1460', color: '#39FF14' } : undefined}
        >
          {creature.isTrading ? '⚖️ Listed' : '📦 Trade'}
        </button>
      </div>
      </div>
    </div>
  )
}
